import { type FC } from "react";
import { DONUT_RING_WIDTH_PX } from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/ViewabilityWidget.module.css";

// The ring is drawn in a 100-unit viewBox rendered at 128px (8rem), so its
// stroke is the shared donut width converted into viewBox units.
const RING_RADIUS = 40;
const RING_BOX_PX = 128;
const VIEWBOX_SIZE = 100;
const RING_STROKE = (DONUT_RING_WIDTH_PX / RING_BOX_PX) * VIEWBOX_SIZE;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const GRAD_ID = "vw-grad-ring";

// NOTE: The design handoff doc describes viewability_rate fields as 0–1.
// The actual Reporting API returns 0–100 (percent scale); confirmed by test
// fixtures (e.g. video_complete_rate: 60.0) and a prior 8000%+ arc fill bug
// caused by double-multiplying. Arc calculations below treat rates as 0–100.

// Impression-weighted average of a per-row rate, or null when no row carries
// impressions.
const impressionWeightedRate = (
  rows: PerformanceReportRow[],
  getRate: (row: PerformanceReportRow) => number
): number | null => {
  const totalImpressions = rows.reduce(
    (sum, row) => sum + (row.impressions ?? 0),
    0
  );
  return totalImpressions > 0
    ? rows.reduce(
        (sum, row) => sum + getRate(row) * (row.impressions ?? 0),
        0
      ) / totalImpressions
    : null;
};

interface ViewabilityWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

const RingTrack: FC = () => (
  <circle
    className={styles.ringTrack}
    cx="50"
    cy="50"
    fill="none"
    r={RING_RADIUS}
    strokeWidth={RING_STROKE}
  />
);

const ViewabilityWidget: FC<ViewabilityWidgetProps> = ({
  error,
  isLoading,
  rows,
}) => {
  const {
    centerCaption,
    errorBody,
    errorHeading,
    noData,
    rate50Note,
    rate100Description,
    rate100UnavailableNote,
    title,
  } = TEXT.campaignOverview.viewability;

  if (isLoading) {
    return (
      <div className={styles.sectionCard}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.metricsRow}>
          <svg className={styles.ring} viewBox="0 0 100 100">
            <RingTrack />
          </svg>
          <div className={styles.shimmerLabel} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.sectionCard} ${styles.sectionCardError}`}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>
            <svg
              fill="none"
              height="22"
              stroke="var(--color-orange)"
              strokeLinecap="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="22"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
          </div>
          <p className={styles.errorHeading}>{errorHeading}</p>
          <p className={styles.errorBody}>{errorBody}</p>
        </div>
      </div>
    );
  }

  // Impression-weighted averages from daily rows.
  // rollup: "by_day" is required — rollup: "all" sums rate fields, producing
  // values 80× the expected range that are meaningless as percentages.
  const rate50 = impressionWeightedRate(
    rows,
    (row) => row.viewability_rate ?? 0
  );

  if (rate50 === null) {
    return (
      <div className={styles.sectionCard}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.emptyState}>
          <p className={styles.emptyBody}>{noData}</p>
        </div>
      </div>
    );
  }

  // Exclude null, undefined, and 0-sentinel rows (API returns 0 for unsupported placements).
  const rate100 = impressionWeightedRate(
    rows.filter(
      (row) =>
        typeof row.viewability_rate_100_percent === "number" &&
        row.viewability_rate_100_percent !== 0
    ),
    (row) => row.viewability_rate_100_percent as number
  );

  const fill = RING_CIRCUMFERENCE * (rate50 / 100);
  const gap = RING_CIRCUMFERENCE - fill;

  return (
    <div className={styles.sectionCard}>
      <span className={styles.sectionLabel}>{title}</span>
      <div className={styles.metricsRow}>
        <svg className={styles.ring} viewBox="0 0 100 100">
          <defs>
            {/* Spec: --grad-bar-v (purple top → plum bottom) across the ring.
                The arc is rotated -90°, which turns the gradient's x axis into
                the visual y axis with 1 at the top — hence x1=1 → x2=0. */}
            <linearGradient id={GRAD_ID} x1="1" x2="0" y1="0" y2="0">
              <stop
                offset="0%"
                style={{ stopColor: "var(--grad-bar-v-start)" }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "var(--grad-bar-v-end)" }}
              />
            </linearGradient>
          </defs>
          <RingTrack />
          <circle
            cx="50"
            cy="50"
            fill="none"
            r={RING_RADIUS}
            stroke={`url(#${GRAD_ID})`}
            strokeDasharray={`${fill.toFixed(1)} ${gap.toFixed(1)}`}
            strokeLinecap="round"
            strokeWidth={RING_STROKE}
            transform="rotate(-90 50 50)"
          />
          <text
            className={styles.centerValue}
            textAnchor="middle"
            x="50"
            y="50"
          >
            {rate50.toFixed(1)}%
          </text>
          <text
            className={styles.centerCaption}
            textAnchor="middle"
            x="50"
            y="66"
          >
            {centerCaption}
          </text>
        </svg>
        <div className={styles.supportingText}>
          <p className={styles.emptyBody}>{rate50Note}</p>
          {rate100 === null ? (
            <p className={styles.emptyBody}>{rate100UnavailableNote}</p>
          ) : (
            <p className={styles.emptyBody}>
              <strong className={styles.supportingValue}>
                {rate100.toFixed(1)}%
              </strong>{" "}
              {rate100Description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewabilityWidget;
